/**
 * Decorative blog h1 presets. Tuned for the marketing parchment page
 * (WEBSITE_PAGE_BACKGROUND #DBD1C0), not the dark CodePen canvas.
 * Empty string is the current untitled look.
 */

export const BLOG_TITLE_STYLE_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'anaglyphic', label: 'Anaglyphic' },
  { value: 'embedded', label: 'Embedded' },
  { value: 'embossed', label: 'Embossed' },
  { value: 'neon', label: 'Neon' },
  { value: 'fire', label: 'Fire' },
  { value: 'retro', label: 'Retro' },
  { value: 'blurred', label: 'Blurred' },
  { value: 'gradient', label: 'Gradient' },
  { value: 'golden', label: 'Golden' },
  { value: 'stitched', label: 'Stitched' },
  { value: 'three_d', label: '3D' },
  { value: 'halloween', label: 'Halloween' },
  { value: 'christmas', label: 'Christmas' },
  { value: 'thanksgiving', label: 'Thanksgiving' },
  { value: 'valentines', label: "Valentine's" },
  { value: 'new_year', label: 'New Year' },
  { value: 'easter', label: 'Easter' },
];

const ALLOWED = new Set(BLOG_TITLE_STYLE_OPTIONS.map(option => option.value));

function gradientText(background) {
  return {
    background,
    WebkitBackgroundClip: 'text',
    backgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    color: 'transparent',
  };
}

const TITLE_STYLE_SX = {
  anaglyphic: {
    color: '#e23d78',
    textShadow: '-3px -3px 0 #3aa0e8',
  },
  embedded: {
    color: '#1a1a1a',
    textShadow: '0 1px 1px rgba(255,255,255,0.65)',
  },
  embossed: {
    color: '#603624',
    textShadow: '-1px -1px 1px rgba(255,255,255,0.55), 1px 1px 1px rgba(0,0,0,0.25)',
  },
  neon: {
    color: '#0e6b73',
    textShadow: '0 0 2px #fff, 0 0 10px #2ee6ef, 0 0 18px rgba(70,253,255,0.85)',
  },
  fire: {
    color: '#9a2b00',
    textShadow: '0 -3px 5px #fffc00, 0 -8px 14px #ff7a1a, 0 -14px 22px rgba(255,90,0,0.55)',
  },
  retro: {
    color: '#c47a32',
    textShadow: '2px 2px 0 #3a3a3a, 4px 4px 0 #8a8a8a',
  },
  blurred: {
    color: '#5c5348',
    textShadow: '0 0 1px #5c5348, 0 0 6px rgba(92,83,72,0.8), 0 0 12px rgba(92,83,72,0.35)',
  },
  gradient: gradientText('linear-gradient(180deg, #3a3a3a, #9a9a9a)'),
  golden: gradientText('linear-gradient(180deg, #f9d400 15%, #c47a00 40%, #f9d400 70%)'),
  stitched: {
    display: 'inline-block',
    position: 'relative',
    color: '#2c241c',
    backgroundColor: '#E6DDD0',
    border: '2px dashed #5c5348',
    padding: '0.15em 0.5em',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 4,
      right: 4,
      bottom: 4,
      left: 4,
      border: '1px dashed rgba(92, 83, 72, 0.7)',
      pointerEvents: 'none',
    },
  },
  three_d: {
    color: '#b50e00',
    textShadow: [
      '1px 1px 0 #510300',
      '2px 2px 0 #510300',
      '3px 3px 0 #510300',
      '4px 4px 0 #510300',
      '5px 5px 0 #510300',
      '6px 6px 0 #510300',
      '7px 7px 0 #510300',
    ].join(', '),
  },
  halloween: {
    color: '#e86a12',
    textShadow: '2px 2px 0 #3b1464, 0 0 12px rgba(255,120,0,0.65)',
  },
  christmas: {
    color: '#b11226',
    textShadow: '2px 2px 0 #1f7a3a, -1px -1px 0 #d4a017',
  },
  thanksgiving: {
    color: '#8c4a2f',
    textShadow: '1px 1px 0 #e6c48a, 2px 2px 0 #c4783a',
  },
  valentines: {
    color: '#c23b6e',
    textShadow: '0 0 8px rgba(255,140,170,0.9), 2px 2px 0 #f3b6c8',
  },
  new_year: gradientText('linear-gradient(180deg, #f0d78c, #c4922a, #6b5014)'),
  easter: gradientText('linear-gradient(180deg, #c44774, #6d4e9c, #2f7a58)'),
};

export function getBlogTitleStyleSx(titleStyle) {
  if (!titleStyle || !ALLOWED.has(titleStyle)) return {};
  return TITLE_STYLE_SX[titleStyle] ?? {};
}
